import { useState, useCallback, useRef } from 'react';
import { checkZipCode } from '../utils/api';
import { debounce, saveZipFlag, getZipFlag } from '../utils/helpers';
import { CapFeedback } from '../types';
import { calculateBonusSummary } from '../config/savingsConfig';

interface UseZipCodeCheckReturn {
  zipFlag: boolean | null;
  isChecking: boolean;
  isValid: boolean | null;
  capFeedback: CapFeedback | null;
  checkZip: (zip: string, productType?: string) => void;
}

/**
 * Hook per verificare i CAP tramite API
 * Gestisce il debouncing e la persistenza in sessionStorage
 * Include feedback sul numero di bonus disponibili
 */
export function useZipCodeCheck(): UseZipCodeCheckReturn {
  const [zipFlag, setZipFlag] = useState<boolean | null>(() => getZipFlag());
  const [isChecking, setIsChecking] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [capFeedback, setCapFeedback] = useState<CapFeedback | null>(null);

  // Debounced check function - 50ms per risposta immediata
  const debouncedCheck = useRef(
    debounce(async (zip: string, productType?: string) => {
      if (!zip || zip.length < 5 || !/^\d{5}$/.test(zip)) {
        setZipFlag(null);
        setIsValid(null);
        setCapFeedback(null);
        saveZipFlag(null);
        setIsChecking(false);
        return;
      }

      setIsChecking(true);

      try {
        const result = await checkZipCode(zip);
        
        if (result !== null) {
          setZipFlag(result);
          setIsValid(true);
          saveZipFlag(result);

          // Calcola il feedback sui bonus
          const summary = calculateBonusSummary(
            productType as 'Photovoltaic' | 'Heatpump' | 'Photovoltaic + Heatpump',
            result,
            zip
          );
          setCapFeedback({
            bonusCount: summary.bonusCount,
            totalSavings: summary.totalSavings,
            isValid: true,
          });

          // Dispatch custom event for other components
          document.dispatchEvent(
            new CustomEvent('enpal:zipChecked', {
              detail: { zip, inList: result },
            })
          );
        } else {
          setZipFlag(null);
          setIsValid(null);
          setCapFeedback(null);
          saveZipFlag(null);
        }
      } catch (error) {
        console.error('Error checking ZIP:', error);
        setZipFlag(null);
        setIsValid(null);
        setCapFeedback(null);
        saveZipFlag(null);
      } finally {
        setIsChecking(false);
      }
    }, 50)
  ).current;

  const checkZip = useCallback(
    (zip: string, productType?: string) => {
      // Reset state quando l'utente modifica il CAP
      if (zip.length < 5) {
        setIsValid(null);
        setCapFeedback(null);
      }
      debouncedCheck(zip, productType);
    },
    [debouncedCheck]
  );

  return {
    zipFlag,
    isChecking,
    isValid,
    capFeedback,
    checkZip,
  };
}
